import { FC } from 'react';

import dayjs from 'dayjs';
import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseFieldArray, BaseInput, FormBodyContainer } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { AvailableTickets } from '@shared/components';
import { getRewardTypeSelectItems, getRewardWithChanceItem } from '@shared/utils';

import { ReferralRewardSettingsFormProps } from './types';
import { referralsSettingsSchema } from './validationSchema';

import styles from './ReferralRewardSettingsForm.module.css';

const { inlineInputsContainer } = styles;

export const ReferralRewardSettingsForm: FC<ReferralRewardSettingsFormProps> = ({
  initialValues,
  isSubmitting = false,
  submitButtonCaption = 'Update',
  onSubmit,
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={onSubmit}
    validationSchema={referralsSettingsSchema}
    enableReinitialize
  >
    {({ submitForm, values, handleChange, handleBlur, errors, setFieldValue, touched }) => {
      const {
        rewardValue,
        freeSpinsCount: freeSpinsCountValue,
        freeSpinsRewardChance: freeSpinsRewardChanceValue,
        firstSpinRewardMinPercent: firstSpinRewardMinPercentValue,
        firstSpinRewardMaxPercent: firstSpinRewardMaxPercentValue,
        defaultSpinRewardMaxPercent: defaultSpinRewardMaxPercentValue,
        defaultSpinRewardMinPercent: defaultSpinRewardMinPercentValue,
        requiredTasksForReferralValidate: requiredTasksForReferralValidateValue,
        rewardsChances: rewardsChancesValue,
        startDate: startDateValue,
      } = values;
      const {
        rewardValue: rewardValueError,
        freeSpinsCount: freeSpinsCountError,
        freeSpinsRewardChance: freeSpinsRewardChanceError,
        firstSpinRewardMinPercent: firstSpinRewardMinPercentError,
        firstSpinRewardMaxPercent: firstSpinRewardMaxPercentError,
        defaultSpinRewardMinPercent: defaultSpinRewardMinPercentError,
        defaultSpinRewardMaxPercent: defaultSpinRewardMaxPercentError,
        requiredTasksForReferralValidate: requiredTasksForReferralValidateError,
      } = errors;

      const onReferralsSettingsSubmit = async () => {
        await submitForm();
      };

      const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

      return (
        <FormBodyContainer
          isSubmitting={!!isSubmitting}
          onSubmit={onReferralsSettingsSubmit}
          submitButtonCaption={submitButtonCaption}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
        >
          <BaseInput
            type="number"
            helperText={rewardValueError}
            id="rewardValue"
            name="rewardValue"
            caption="Reward Value"
            label="Reward Value"
            value={rewardValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={!!rewardValueError}
          />
          <Typography fontSize="16px" fontWeight={600}>
            Default spin reward size
          </Typography>
          <Box className={inlineInputsContainer}>
            <BaseInput
              type="number"
              helperText={freeSpinsCountError}
              id="freeSpinsCount"
              name="freeSpinsCount"
              caption="Free Spins Count"
              label="Free Spins Count"
              value={freeSpinsCountValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!freeSpinsCountError}
            />
            <BaseInput
              type="number"
              helperText={freeSpinsRewardChanceError}
              id="freeSpinsRewardChance"
              name="freeSpinsRewardChance"
              caption="Free Spins Count"
              label="Free Spins Reward Chance"
              value={freeSpinsRewardChanceValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!freeSpinsRewardChanceError}
            />
          </Box>
          <Typography fontSize="16px" fontWeight={600}>
            First spin (welcome bonus) reward size
          </Typography>
          <Box className={inlineInputsContainer}>
            <BaseInput
              type="number"
              helperText={firstSpinRewardMinPercentError}
              id="firstSpinRewardMinPercent"
              name="firstSpinRewardMinPercent"
              caption="Min value (%)"
              label="Min value (%)"
              value={firstSpinRewardMinPercentValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!firstSpinRewardMinPercentError}
            />
            <BaseInput
              type="number"
              helperText={firstSpinRewardMaxPercentError}
              id="firstSpinRewardMaxPercent"
              name="firstSpinRewardMaxPercent"
              caption="Max value (%)"
              label="Max value (%)"
              value={firstSpinRewardMaxPercentValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!firstSpinRewardMaxPercentError}
            />
          </Box>
          <Typography fontSize="16px" fontWeight={600}>
            Default spin reward size
          </Typography>
          <Box className={inlineInputsContainer}>
            <BaseInput
              type="number"
              helperText={defaultSpinRewardMinPercentError}
              id="defaultSpinRewardMinPercent"
              name="defaultSpinRewardMinPercent"
              caption="Min value (%)"
              label="Min value (%)"
              value={defaultSpinRewardMinPercentValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!defaultSpinRewardMinPercentError}
            />
            <BaseInput
              type="number"
              helperText={defaultSpinRewardMaxPercentError}
              id="defaultSpinRewardMaxPercent"
              name="defaultSpinRewardMaxPercent"
              caption="Max value (%)"
              label="Max value (%)"
              value={defaultSpinRewardMaxPercentValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!defaultSpinRewardMaxPercentError}
            />
          </Box>
          <BaseInput
            type="number"
            helperText={requiredTasksForReferralValidateError}
            id="requiredTasksForReferralValidate"
            name="requiredTasksForReferralValidate"
            caption="Required Tasks For Spin"
            label="Required Tasks For Spin"
            value={requiredTasksForReferralValidateValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={!!requiredTasksForReferralValidateError}
          />
          <Typography fontSize="16px" fontWeight={600}>
            Rewards (Referral money any value)
          </Typography>
          <AvailableTickets rewardsDataValue={rewardsChancesValue} />
          <BaseFieldArray
            fieldArrayName="rewardsChances"
            items={rewardsChancesValue}
            getBaseFieldValue={getRewardWithChanceItem}
            selectSettings={{
              selectFields: { type: getRewardTypeSelectItems() },
            }}
            touched={touched}
            errors={errors}
            label="Reward"
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
          />
          <Typography fontSize="18px" fontWeight={600}>
            Referral system start date: {dayjs(startDateValue).format('YYYY-MM-DD HH:mm:ss')}
          </Typography>
          <Typography fontSize="14px">(Referrals are counted starting from this date.)</Typography>
        </FormBodyContainer>
      );
    }}
  </Formik>
);
